import { useSelf } from "@rentiohq/shared-frontend/dist/redux/auth/auth.hooks";
import api from "@rentiohq/shared-frontend/dist/utils/api/api.utils";
import {
  getLocale,
  getLocalizedText,
} from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  TextStyle,
  Loading,
  ELoadingSize,
  Modal,
  Tooltip,
} from "@rentiohq/web-shared/dist/components";
import { toast } from "@rentiohq/web-shared/dist/managers/Toast";
import styled from "@rentiohq/web-theme";
import moment from "moment";
import "moment/locale/nl-be";
import "moment/locale/fr";
import "moment/locale/en-gb";
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
// @ts-ignore
import { Calendar, momentLocalizer } from "react-big-calendar";
// @ts-ignore
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import {
  BOOKING_ANCHOR_KEY,
  BOOKING_ANCHORS,
  getCronofyStatus,
  resolveNewBlockAnchor,
  resolveSavedBlockAnchor,
  TBookingAnchor,
} from "utils/cronofy.utils";
import { RM_COLORS } from "../rentmatch.colors";
import { WarningTooltip, WarningTriangle } from "./WarningTooltip";

const localizer = momentLocalizer(moment);
const DnDCalendar = withDragAndDrop(Calendar);

// ========== STYLED COMPONENTS ==========

const PageLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  /* Bounded by whatever holds it, so the scrolling pill row below clips at
     the modal's edge rather than running past it. */
  min-width: 0;
`;

const ControlsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-width: 0;
  background: ${RM_COLORS.gray50};
  padding: 16px;
  border-radius: 8px;
  border: 1px solid ${RM_COLORS.gray200};
`;

/**
 * `min-width: 0` on every step between the modal and the scrolling pill row.
 *
 * A flex item refuses to shrink below its own content unless told otherwise,
 * so one unbounded ancestor is enough to push the row past the modal's edge:
 * the pills scrolled, but the box they scrolled in was already wider than the
 * window (Jonas, 2026-10-03). This is the row that holds the three control
 * groups, and it is the one that has to give.
 */
const FormRow = styled.div`
  display: flex;
  gap: 24px;
  align-items: flex-start;
  flex: 1;
  min-width: 0;
  /* The slot length and visitor count cannot shrink, so below a certain
     width the row's minimum exceeds the modal. It wraps instead of
     scrolling: a scroll container inside a scroll container leaves nobody
     able to tell which one the wheel is moving (Jonas, 2026-10-03). */
  flex-wrap: wrap;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FormLabel = styled.span`
  font-size: ${p => p.theme.fontSizes[1]}px;
  font-weight: 400;
  color: ${p => p.theme.colors.neutral90};
`;

const SelectInput = styled.select`
  padding: 0 12px;
  height: 36px;
  border-radius: 4px;
  border: 1px solid ${RM_COLORS.gray300};
  background: white;
  min-width: 150px;
  font-size: ${p => p.theme.fontSizes[1]}px;
  color: ${p => p.theme.colors.neutral90};
`;

/**
 * The group holding the calendars, which is the only one that can outgrow the
 * row. It is allowed to shrink (a flex item will not go below its content
 * without `min-width: 0`) so the pills inside can scroll instead of pushing
 * the last one out of the modal (Jonas, 2026-10-03).
 */
const CalendarsGroup = styled(FormGroup)`
  /* Takes the rest of the row when it fits, and drops to a line of its own
     when there is less than this left. The zero minimum still applies after
     the wrap, so the pills inside keep scrolling rather than pushing out. */
  flex: 1 1 260px;
  min-width: 0;
`;

/**
 * The scrolling pills and the add button side by side.
 *
 * The button sits outside the scroller on purpose. Its dropdown is positioned
 * against it, and a scroll container clips anything that hangs out of it, so
 * inside the row the dropdown was invisible (Jonas, 2026-10-03). Keeping it
 * out also means the button stays reachable once the row is long enough to
 * scroll, which is exactly when a broker wants to add someone.
 */
const PillsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const PillsContainer = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  gap: 8px;
  align-items: center;
  overflow-x: auto;
  overflow-y: hidden;

  /* Hidden scrollbar, as on the tab strip behind the modal. */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

/**
 * The pill is a wrapper rather than the button it used to be: the shared
 * Tooltip renders a real button of its own, and a button inside a button is
 * neither valid nor clickable the way anyone expects. The name carries the
 * selection, the warning and the remove control sit beside it (DEV-3320).
 */
const Pill = styled.div<{ active: boolean; color: string }>`
  flex-shrink: 0;
  padding: 0 16px;
  height: 36px;
  border-radius: 20px;
  border: 2px solid ${p => (p.active ? p.color : "transparent")};
  background: ${p => (p.active ? p.color + "20" : RM_COLORS.gray100)};
  color: ${p => (p.active ? p.color : p.theme.colors.neutral90)};
  font-size: ${p => p.theme.fontSizes[1]}px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;

  &:before {
    content: "";
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${p => p.color};
  }
`;

const PillSelect = styled.button`
  flex: 1;
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  font: inherit;
  color: inherit;
  text-align: left;
  white-space: nowrap;
  cursor: pointer;
`;

const AddPillWrapper = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const AddPillButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px dashed ${RM_COLORS.gray400};
  background: white;
  color: ${RM_COLORS.gray500};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  &:hover {
    border-color: ${RM_COLORS.blue};
    color: ${RM_COLORS.blue};
  }
`;

const BrokerDropdown = styled.div`
  position: absolute;
  top: 44px;
  /* Hangs from the button's right edge, because the button sits at the end
     of the row: opening to the right ran it off the modal (Jonas,
     2026-10-03). */
  right: 0;
  background: white;
  border-radius: 8px;
  box-shadow:
    0 4px 24px rgba(0, 0, 0, 0.12),
    0 0 0 1px rgba(63, 63, 68, 0.1);
  z-index: 100;
  min-width: 220px;
  overflow: hidden;
`;

const DropdownItem = styled.button<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 14px;
  border: none;
  background: none;
  cursor: ${p => (p.disabled ? "default" : "pointer")};
  font-size: 1.2rem;
  color: ${p => (p.disabled ? RM_COLORS.gray400 : RM_COLORS.gray700)};
  text-align: left;
  &:hover {
    background: ${p => (p.disabled ? "none" : RM_COLORS.gray100)};
  }
`;

const DropdownDot = styled.div<{ color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${p => p.color};
  flex-shrink: 0;
`;

const RemovePillButton = styled.span`
  margin-left: 6px;
  font-size: 1.2rem;
  opacity: 0.5;
  cursor: pointer;
  &:hover {
    opacity: 1;
  }
`;

const CalendarWrapper = styled.div`
  display: flex;
  gap: 20px;
`;

const CalendarContainer = styled.div`
  flex: 1;
  /* Size to the viewport so the modal fits without its own scrollbar on normal
     screens; keep a usable floor so it scrolls inside the modal only on very
     small screens. ~280px accounts for the modal header, toolbar and padding. */
  height: clamp(320px, calc(100vh - 280px), 600px);

  .rbc-calendar {
    font-size: 1.2rem;
    color: ${RM_COLORS.gray900};
  }

  .rbc-toolbar button {
    font-family: inherit;
    font-size: 1.2rem;
    color: ${RM_COLORS.gray700};
  }

  .rbc-toolbar-label {
    font-size: 1.4rem;
    font-weight: 600;
    color: ${RM_COLORS.gray900};
  }

  .rbc-event {
    border-radius: 4px;
    cursor: pointer;
  }

  .rbc-event.readonly {
    opacity: 0.35;
    cursor: default;
    background-image: repeating-linear-gradient(
      45deg,
      transparent,
      transparent 8px,
      rgba(255, 255, 255, 0.4) 8px,
      rgba(255, 255, 255, 0.4) 16px
    );
  }
`;

const Sidebar = styled.div`
  width: 280px;
  background: ${RM_COLORS.gray50};
  border: 1px solid ${RM_COLORS.gray200};
  border-radius: 8px;
  padding: 16px;
  /* Keep in sync with CalendarContainer so both columns stay the same height. */
  max-height: clamp(320px, calc(100vh - 280px), 600px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const SidebarSlotsList = styled.div`
  /* Scrolls on its own so the total stays pinned; grow:0 keeps the total snug
     under the list when there are few slots instead of pushing it to the bottom. */
  flex: 0 1 auto;
  min-height: 0;
  overflow-y: auto;
`;

const SidebarTitle = styled.h3`
  margin: 0 0 16px 0;
  font-size: 1.4rem;
  font-weight: 600;
  color: ${RM_COLORS.gray900};
  flex-shrink: 0;
`;

const DayCard = styled.div`
  background: white;
  border: 1px solid ${RM_COLORS.gray200};
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
`;

const DayHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const DayLabel = styled.span`
  font-weight: 600;
  font-size: 1.2rem;
  color: ${RM_COLORS.green};
  text-transform: uppercase;
`;

const SlotItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 4px;
  padding: 4px 0;
  font-size: 1.2rem;
  color: ${RM_COLORS.gray700};
`;

const SlotItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

// Narrow, quiet and indented under the moment it belongs to: it is read far
// more often than it is changed, and a full-width control per block would
// make a day of six blocks a wall of dropdowns.
const SlotAnchorSelect = styled.select`
  margin-left: 2px;
  padding: 2px 4px;
  font-size: 1.1rem;
  color: ${RM_COLORS.gray700};
  background: ${RM_COLORS.white};
  border: 1px solid ${RM_COLORS.gray300};
  border-radius: 4px;
  cursor: pointer;
`;

const DeleteSlotBtn = styled.button`
  background: none;
  border: none;
  color: ${RM_COLORS.gray300};
  cursor: pointer;
  font-size: 1.2rem;
  padding: 0 2px;
  &:hover {
    color: ${RM_COLORS.red};
  }
`;

// Red when the blocks that are drawn yield no bookable time at all, in the same
// background/text pair this product uses for every other error state
// (DEV-3220). Green says "this is set up"; a green card reading zero was the
// screen contradicting itself.
const TotalCard = styled.div<{ $isEmpty?: boolean }>`
  background: ${({ $isEmpty }) =>
    $isEmpty ? RM_COLORS.redBg : RM_COLORS.greenBg};
  border: 1px solid
    ${({ $isEmpty }) => ($isEmpty ? RM_COLORS.red : RM_COLORS.greenBorder)};
  border-radius: 8px;
  padding: 12px;
  /* Pinned footer: stays visible while the slots list scrolls above it. The
     top spacing keeps scrolling day cards from bumping against it. */
  flex-shrink: 0;
  margin-top: 16px;
`;

// ========== INTERFACES ==========

interface IBroker {
  id: string;
  name: string;
  color: string;
  /**
   * Whether this colleague has a personal calendar connected. Moments may be
   * drawn for anyone in the office either way (DEV-3320); the difference is
   * what Rentio can do with them, which the screen says out loud rather than
   * leaving the broker to discover.
   */
  hasConnectedCalendar: boolean;
  /**
   * Set for a colleague who holds saved moments but is no longer in the
   * office roster. Their moments stay drawn and are marked for repair, since
   * silently dropping them would lose bookable time nobody asked to remove.
   */
  hasLeftOffice?: boolean;
}

export interface ICalendarSlot {
  id: string;
  title: string;
  start: Date;
  end: Date;
  userId: string;
  color: string;
  isReadonly?: boolean;
  isRentioVisit?: boolean;
  // Which free moments this block offers. Per block rather than per property:
  // a Saturday afternoon of back to back visits and a weekday evening left
  // open are the same property on the same day (Jonas, 2026-10-06).
  bookingAnchor?: TBookingAnchor;
}

/**
 * Which free moments a candidate is offered inside a block (DEV-3328).
 *
 * START and END keep the employee's visits back to back, filling from one
 * end of the block; FREE offers every free moment, which is what every block
 * did before this existed and what leaves the broker waiting between two
 * bookings. The rule itself is applied server-side, so no client can offer
 * or book a moment it narrows away; this is only where the broker says which
 * of the three a block uses.
 */
export type { TBookingAnchor };

/**
 * The slot lengths an office can pick. Ten is the shortest a visit is worth
 * scheduling and was asked for by brokers doing short viewings (Jonas,
 * 2026-10-06); the rest are what the toggle offered before.
 *
 * Note the calendar itself is drawn on a fifteen-minute grid, so a block is
 * still dragged to quarter hours; the slot length only divides whatever
 * block was drawn.
 */
export const SLOT_DURATIONS = [10, 15, 20, 30, 60];

interface IPropertyNativeCalendarProps {
  propertyId: number;
  propertyAddress?: string;
  onUpdateSlots: (
    slots: {
      start: string;
      end: string;
      userId: string;
      slotDurationMinutes?: number;
      capacityPerSlot?: number;
      bookingAnchor?: TBookingAnchor;
    }[],
  ) => void;
  onDirty?: () => void;
  defaultBrokerUserId?: number;
  onRemainingCountChange?: (remaining: number) => void;
}

// Calendar events (external busy blocks, planned visits, availability slots)
// may carry extra descriptive fields from the connected calendar.
type ICalendarEventDisplay = ICalendarSlot & {
  summary?: string;
  location?: string;
};

/**
 * Custom react-big-calendar event renderer. Shows the event title in the block
 * and a hover tooltip revealing the full title plus, when available, the
 * address/location of the appointment — so a broker can see what an existing
 * (busy) appointment is without leaving the planner. Defined at module scope so
 * it is not recreated on every calendar render.
 */
// react-big-calendar types `components.event` as `EventProps<object>`, so accept
// the loose `object` shape it passes and narrow to our display type internally.
const CalendarEvent: React.FC<{ event: object }> = ({ event }) => {
  const { title, summary, location } = event as ICalendarEventDisplay;
  // The block keeps its short label (e.g. "Not available (Broker)"); on hover we
  // reveal the actual appointment content — the real summary and location when
  // the calendar provides them, falling back to the label otherwise.
  const tooltipTitle = summary || title;
  return (
    <Tooltip
      placement="top"
      tooltipContentStyle={{
        backgroundColor: RM_COLORS.gray900,
        color: RM_COLORS.white,
        padding: "6px 10px",
        borderRadius: "6px",
        fontSize: "12px",
        lineHeight: "1.4",
        maxWidth: "260px",
      }}
      tooltipContent={
        <div>
          <div style={{ fontWeight: 600 }}>{tooltipTitle}</div>
          {location && (
            <div style={{ opacity: 0.85, marginTop: "2px" }}>{location}</div>
          )}
        </div>
      }
    >
      <span
        style={{
          display: "block",
          width: "100%",
          height: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {title}
      </span>
    </Tooltip>
  );
};

// ========== COMPONENT ==========

export const PropertyNativeCalendar: React.FC<IPropertyNativeCalendarProps> = ({
  propertyId,
  propertyAddress,
  onUpdateSlots,
  onDirty,
  defaultBrokerUserId,
  onRemainingCountChange,
}) => {
  // Ensure moment uses the correct user language for all native translations
  moment.locale(
    String(getLocale()) === "en_US"
      ? "en-gb"
      : String(getLocale()).replace("_", "-").toLowerCase(),
  );

  const { user: currentUser } = useSelf();

  const [events, setEvents] = useState<ICalendarSlot[]>([]);
  const [bookedVisits, setBookedVisits] = useState<any[]>([]);
  const [isLoadingBrokers, setIsLoadingBrokers] = useState(true);
  const [isLoadingEvents, setIsLoadingEvents] = useState(true);

  const [brokers, setBrokers] = useState<IBroker[]>([]);
  // Kept apart from "no brokers", because the two say opposite things to the
  // person reading them: one means go connect a calendar, the other means the
  // request failed and we have no idea what they have (DEV-3209).
  const [hasBrokerLoadError, setHasBrokerLoadError] = useState(false);
  const [allConnections, setAllConnections] = useState<IBroker[]>([]);
  const [activeBrokerId, setActiveBrokerId] = useState<string>("");
  const [showBrokerDropdown, setShowBrokerDropdown] = useState(false);
  const [pendingRemoveBrokerId, setPendingRemoveBrokerId] = useState<
    string | null
  >(null);
  // The moment whose colleague is being repaired, and the colleague picked in
  // that dialog (DEV-3320).
  const [slotToReassignId, setSlotToReassignId] = useState<string | null>(null);
  const [reassignToBrokerId, setReassignToBrokerId] = useState<string>("");
  const [duration, setDuration] = useState<number>(15);
  const [capacity, setCapacity] = useState<number>(1);
  // What a block drawn from here on starts on. Not what the property uses:
  // every block carries its own choice, and this is only the one a new block
  // is born with.
  const [defaultBookingAnchor, setDefaultBookingAnchor] =
    useState<TBookingAnchor>("START");

  const activeBroker = useMemo(
    () => brokers.find(b => b.id === activeBrokerId),
    [brokers, activeBrokerId],
  );

  // Editable (non-readonly) slots for the sidebar
  const editableSlots = useMemo(
    () => events.filter(e => !e.isReadonly),
    [events],
  );
  const busyEvents = useMemo(() => events.filter(e => e.isReadonly), [events]);

  // Count available and booked slots in a block, excluding time that overlaps with busy events
  const countRemainingSlots = useCallback(
    (slot: ICalendarSlot) => {
      let theoretical = 0;
      let booked = 0;
      const now = moment();
      const slotStart = moment(slot.start);
      const slotEnd = moment(slot.end);
      const cursor = slotStart.clone();

      while (cursor.clone().add(duration, "minutes").isSameOrBefore(slotEnd)) {
        const windowStart = cursor.clone();
        const windowEnd = cursor.clone().add(duration, "minutes");

        // Expired windows (already in the past) are no longer available
        // capacity — do not count them as bookable slots.
        if (windowEnd.isSameOrBefore(now)) {
          cursor.add(duration, "minutes");
          continue;
        }

        // Check overlap with busy events from the same broker only.
        // Other brokers' busy times should not affect this slot's availability.
        const overlapsBusy = busyEvents.some(busy => {
          if (busy.isRentioVisit) return false; // Native viewings do NOT block the capacity tracker
          if (busy.userId !== slot.userId) return false;
          const busyStart = moment(busy.start);
          const busyEnd = moment(busy.end);
          return windowStart.isBefore(busyEnd) && windowEnd.isAfter(busyStart);
        });

        if (!overlapsBusy) {
          theoretical += capacity;
          const bookedInWindow = bookedVisits.filter(v => {
            const vStart = moment(v.start);
            const vEnd = moment(v.end);
            return (
              vStart.isSameOrAfter(windowStart) &&
              vEnd.isSameOrBefore(windowEnd)
            );
          }).length;
          booked += bookedInWindow;
        }
        cursor.add(duration, "minutes");
      }
      return { theoretical, booked };
    },
    [busyEvents, duration, capacity, bookedVisits],
  );

  // Group editable slots by day for the sidebar
  const slotsByDay = useMemo(() => {
    const grouped: Record<string, ICalendarSlot[]> = {};
    editableSlots.forEach(slot => {
      const dayKey = moment(slot.start).format("YYYY-MM-DD");
      if (!grouped[dayKey]) grouped[dayKey] = [];
      grouped[dayKey].push(slot);
    });
    const sortedKeys = Object.keys(grouped).sort();
    return sortedKeys.map(key => ({
      dayKey: key,
      label: moment(key).format("dddd D MMMM"),
      slots: grouped[key].sort((a, b) => a.start.getTime() - b.start.getTime()),
    }));
  }, [editableSlots]);

  // Count total available and booked slots across all editable blocks
  const totalCounts = useMemo(() => {
    let t = 0;
    let b = 0;
    editableSlots.forEach(slot => {
      const counts = countRemainingSlots(slot);
      t += counts.theoretical;
      b += counts.booked;
    });
    return { theoretical: t, booked: b };
  }, [editableSlots, countRemainingSlots]);

  useEffect(() => {
    if (onRemainingCountChange) {
      onRemainingCountChange(totalCounts.theoretical - totalCounts.booked);
    }
  }, [totalCounts, onRemainingCountChange]);

  // Load broker connections
  const BROKER_COLORS = [
    RM_COLORS.green, // emerald
    RM_COLORS.blue, // blue
    RM_COLORS.amber, // amber
    RM_COLORS.purple, // violet
    RM_COLORS.red, // red
    RM_COLORS.brandAccent, // pink
    RM_COLORS.teal, // teal
    RM_COLORS.orange, // orange
  ];

  useEffect(() => {
    const loadBrokers = async () => {
      try {
        setIsLoadingBrokers(true);
        setHasBrokerLoadError(false);
        // The roster is every employee of the office, connected or not
        // (DEV-3320). It used to be the list of connected calendars, which
        // made a colleague without one invisible and therefore impossible to
        // draw moments for.
        const [employeesRes, savedRes] = await Promise.all([
          api.get<any>("/cronofy/office-employees", { isV2: true }),
          api.get<any>(`/cronofy/properties/${propertyId}/availability`, {
            isV2: true,
          }),
        ]);

        const employees = employeesRes.data?.data || employeesRes.data || [];
        const roster: IBroker[] = [];
        const seenIds = new Set<string>();

        employees.forEach((e: any) => {
          const id = String(e.userId);
          if (seenIds.has(id)) {
            return;
          }
          seenIds.add(id);
          roster.push({
            id,
            name: e.name || `#${id}`,
            color: BROKER_COLORS[roster.length % BROKER_COLORS.length],
            hasConnectedCalendar: !!e.hasConnectedCalendar,
          });
        });
        setAllConnections(roster);

        const savedBlocksList =
          savedRes.data?.data?.blocks ||
          savedRes.data?.data ||
          savedRes.data ||
          [];
        const savedUserIds: string[] = [
          ...new Set<string>(
            savedBlocksList
              .map((b: any) => (b.userId ? String(b.userId) : ""))
              .filter(Boolean),
          ),
        ];

        const currentUserId = String(currentUser?.id);
        const shownIds = new Set([
          ...savedUserIds,
          currentUserId,
          ...(defaultBrokerUserId ? [String(defaultBrokerUserId)] : []),
        ]);

        const shown: IBroker[] = roster.filter(b => shownIds.has(b.id));

        // A colleague who holds saved moments but is not on the roster any
        // more has left the office. Their moments stay drawn and get marked,
        // because dropping them silently would remove bookable time nobody
        // asked to remove.
        savedUserIds.forEach(uid => {
          if (shown.some(b => b.id === uid)) {
            return;
          }
          const block = savedBlocksList.find(
            (b: any) => String(b.userId) === uid,
          );
          shown.push({
            id: uid,
            name: block?.brokerName || `#${uid}`,
            color: BROKER_COLORS[shown.length % BROKER_COLORS.length],
            hasConnectedCalendar: !!block?.hasConnectedCalendar,
            hasLeftOffice: true,
          });
        });

        if (shown.length > 0) {
          setBrokers(shown);
          const preferredUserId = defaultBrokerUserId
            ? String(defaultBrokerUserId)
            : currentUserId;
          // The person drawing is the default, so the common case needs no
          // choice at all (DEV-3320).
          setActiveBrokerId(
            (
              shown.find(b => b.id === currentUserId) ||
              shown.find(b => b.id === preferredUserId) ||
              shown[0]
            ).id,
          );
        } else if (roster.length > 0) {
          setBrokers([roster[0]]);
          setActiveBrokerId(roster[0].id);
        }
      } catch (e) {
        console.error("Failed to load office employees", e); // eslint-disable-line no-console
        setBrokers([]);
        setHasBrokerLoadError(true);
      } finally {
        setIsLoadingBrokers(false);
      }
    };
    loadBrokers();
  }, [propertyId]);

  const availableBrokersToAdd = useMemo(
    () => allConnections.filter(c => !brokers.some(b => b.id === c.id)),
    [allConnections, brokers],
  );

  const addBrokerToCalendar = (broker: IBroker) => {
    setBrokers(prev => [...prev, broker]);
    setActiveBrokerId(broker.id);
    setShowBrokerDropdown(false);
  };

  const removeBrokerFromCalendar = (brokerId: string) => {
    setPendingRemoveBrokerId(brokerId);
  };

  const confirmRemoveBroker = () => {
    if (!pendingRemoveBrokerId) return;
    const brokerId = pendingRemoveBrokerId;
    setPendingRemoveBrokerId(null);

    const newEvents = events.filter(e => e.userId !== brokerId);
    setEvents(newEvents);
    syncToParent(newEvents);
    if (onDirty) onDirty();

    setBrokers(prev => {
      const updated = prev.filter(b => b.id !== brokerId);
      if (activeBrokerId === brokerId && updated.length > 0) {
        setActiveBrokerId(updated[0].id);
      }
      return updated;
    });
  };

  // Close dropdown on click outside
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!showBrokerDropdown) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setShowBrokerDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showBrokerDropdown]);

  // Load busy events + saved availability blocks
  useEffect(() => {
    if (brokers.length === 0) return;

    const loadBackgroundData = async () => {
      try {
        setIsLoadingEvents(true);
        const brokerIds = brokers.map(b => b.id).join(",");
        const from = moment().startOf("week").toISOString();
        const to = moment().add(1, "month").endOf("week").toISOString();

        // cspell:ignore freebusy
        // freebusy is non-fatal: a 401 JWT refresh or Cronofy auth failure
        // should not wipe the availability slots from the sidebar.
        const [busyRes, savedRes] = await Promise.all([
          api
            .get<any>(
              `/cronofy/properties/${propertyId}/freebusy?brokerIds=${brokerIds}&from=${from}&to=${to}`,
              { isV2: true },
            )
            .catch(() => ({ data: [] })),
          api.get<any>(`/cronofy/properties/${propertyId}/availability`, {
            isV2: true,
          }),
        ]);

        const busyBlocks = (busyRes.data?.data || busyRes.data || []).map(
          (b: any) => ({
            ...b,
            start: new Date(b.start),
            end: new Date(b.end),
          }),
        );

        const savedBlocksList =
          savedRes.data?.data?.blocks ||
          savedRes.data?.data ||
          savedRes.data ||
          [];
        // The read hands back every appointment a property ever had, cancelled
        // ones included. A cancelled one is neither drawn on the calendar nor
        // counted as a slot taken: nobody is coming.
        //
        // Appointments in the past are kept, deliberately. They occupied a slot
        // in a window that has been and gone, and both halves of the counter
        // cover past windows, so dropping them would read a fully booked day
        // back as empty.
        const visitsList = (savedRes.data?.data?.visits || []).filter(
          (v: any) => v.status !== "cancelled",
        );
        setBookedVisits(visitsList);

        const mappedVisits = visitsList.map((v: any) => {
          const visitBroker = brokers.find(br => br.id === String(v.userId));
          return {
            id: `visit-${v.id}`,
            title: getLocalizedText("rentmatch.calendar_planned_visit"),
            start: new Date(v.start),
            end: new Date(v.end),
            userId: String(v.userId),
            color: visitBroker?.color || RM_COLORS.blue,
            status: "busy",
            isReadonly: true, // Bookings cannot be manually dragged
            isRentioVisit: true,
          };
        });

        // Google Free/Busy will return 'Busy' slots for Rentio visit events
        // (already booked viewings we track ourselves). We exclude those to
        // avoid double-counting them as external conflicts.
        // Note: we do NOT filter shadow availability blocks here — doing so
        // would incorrectly hide real conflicts from other properties whose
        // shadow events happen to overlap a newly saved block for this property.
        const filteredBusyBlocks = busyBlocks.filter((b: any) => {
          // Skip events that mirror a Rentio-tracked visit
          if (
            mappedVisits.some(
              (v: any) =>
                v.userId === String(b.brokerId) &&
                Math.abs(v.start.getTime() - new Date(b.start).getTime()) <
                  60000 &&
                Math.abs(v.end.getTime() - new Date(b.end).getTime()) < 60000,
            )
          )
            return false;

          return true;
        });

        // Assign each busy block the color of the broker it belongs to. A
        // colleague who shares their calendar contents (DEV-3540) sends the
        // event's title as `summary`; the block then shows it instead of
        // "not available", and the tooltip adds the location.
        const coloredBusyBlocks = filteredBusyBlocks.map((b: any) => {
          const busyBroker = brokers.find(br => br.id === String(b.brokerId));
          const blockLabel =
            b.summary || getLocalizedText("rentmatch.calendar_not_available");
          return {
            ...b,
            title: busyBroker
              ? `${blockLabel} (${busyBroker.name})`
              : blockLabel,
            userId: String(b.brokerId),
            color: busyBroker?.color || RM_COLORS.gray400,
            isReadonly: true,
          };
        });

        const savedBlocks = savedBlocksList.map((b: any) => {
          const broker = brokers.find(br => br.id === String(b.userId));
          const titlePrefix = propertyAddress
            ? `${getLocalizedText(
                "rentmatch.calendar_placeholder_visit",
              )} ${propertyAddress}`
            : getLocalizedText("rentmatch.calendar_placeholder_visit");
          return {
            id: b.id,
            title: `${titlePrefix} (${
              broker?.name || b.brokerName || "Broker"
            })`,
            start: new Date(b.start),
            end: new Date(b.end),
            userId: String(b.userId),
            color: broker?.color || RM_COLORS.gray400,
            isReadonly: false,
            bookingAnchor: resolveSavedBlockAnchor(b.bookingAnchor),
          };
        });

        // Length and visitors per slot stay property-wide, so they are
        // still read from whatever block was saved first.
        if (savedBlocksList.length > 0) {
          const firstBlock = savedBlocksList[0];
          if (firstBlock.slotDurationMinutes)
            setDuration(Number(firstBlock.slotDurationMinutes));
          if (firstBlock.capacityPerSlot)
            setCapacity(Number(firstBlock.capacityPerSlot));
        }

        try {
          // Reading the connection is `status`; writing it is `connection`.
          // Asking the write route for a read answered 404, and the catch
          // below swallowed it, so the default never prefilled and nothing
          // said why (DEV-3328).
          //
          // Read even where the property already has blocks: this seeds the
          // next block drawn, not the ones already on the calendar.
          const preferred = (await getCronofyStatus())
            ?.visitBookingAnchorDefault;
          setDefaultBookingAnchor(resolveNewBlockAnchor(preferred));
        } catch (e) {
          // The employee default is a convenience, not a requirement: a
          // failed read leaves a new block on START, which is what an
          // employee without a default gets anyway.
        }

        const activeBrokerIds = new Set(brokers.map(b => b.id));
        setEvents([
          ...coloredBusyBlocks.filter((b: ICalendarSlot) =>
            activeBrokerIds.has(b.userId),
          ),
          ...savedBlocks.filter((b: ICalendarSlot) =>
            activeBrokerIds.has(b.userId),
          ),
          ...mappedVisits,
        ]);
      } catch (e) {
        console.error("Failed to load calendar data", e); // eslint-disable-line no-console
        setEvents([]);
      } finally {
        setIsLoadingEvents(false);
      }
    };

    loadBackgroundData();
  }, [propertyId, brokers]);

  // Notify parent whenever editable slots change
  const syncToParent = useCallback(
    (updatedEvents: ICalendarSlot[]) => {
      const savableSlots = updatedEvents
        .filter(e => !e.isReadonly)
        .map(e => ({
          start: e.start.toISOString(),
          end: e.end.toISOString(),
          userId: e.userId,
          slotDurationMinutes: duration,
          capacityPerSlot: capacity,
          // Its own choice, falling back to what a new block would get: a
          // block that somehow reaches here without one must not be saved
          // as "no rule", which the API reads as every free moment.
          bookingAnchor: e.bookingAnchor || defaultBookingAnchor,
        }));
      onUpdateSlots(savableSlots);
    },
    [duration, capacity, defaultBookingAnchor, onUpdateSlots],
  );

  // Sync to parent immediately when duration or capacity settings change
  useEffect(() => {
    syncToParent(events);
  }, [duration, capacity, syncToParent]);

  const snapEndToDuration = useCallback(
    (start: Date, end: Date): Date => {
      const diffMins = moment(end).diff(moment(start), "minutes");
      let multiples = Math.round(diffMins / duration);
      if (multiples < 1) multiples = 1; // minimum 1 block length
      return moment(start)
        .add(multiples * duration, "minutes")
        .toDate();
    },
    [duration],
  );

  // A block laid over an appointment in the broker's own calendar yields no
  // bookable minute: the server cuts that time out of the offer before a
  // candidate ever sees it. So it is refused where it is made, next to the two
  // refusals that were already here for the past and for overlapping another
  // block (DEV-3220). Rentio's own visits are skipped, same rule as the
  // capacity counter, or every booked viewing would block its own slot.
  const overlapsBusyForUser = useCallback(
    (start: Date, end: Date, userId: string) =>
      busyEvents.some(busy => {
        if (busy.isRentioVisit) return false;
        if (busy.userId !== userId) return false;
        return (
          moment(start).isBefore(busy.end) && moment(end).isAfter(busy.start)
        );
      }),
    [busyEvents],
  );

  const handleSelectSlot = useCallback(
    ({ start, end, action }: { start: Date; end: Date; action: string }) => {
      if (!activeBroker) return;
      if (action === "click") return; // Single-click ignores. Drag and double-click are allowed.

      // Auto-snap the end to a multiple of the configured duration.
      const snappedEnd = snapEndToDuration(start, end);

      // Disallow creating slots in the past — a viewing can't be scheduled for
      // a moment that has already passed.
      if (moment(start).isBefore(moment())) {
        toast({
          heading: getLocalizedText("rentmatch.calendar_past_slot_error"),
          variation: "error",
        });
        return;
      }

      // Disallow overlap with existing blocks of any broker
      const overlap = editableSlots.some(
        s =>
          moment(start).isBefore(s.end) && moment(snappedEnd).isAfter(s.start),
      );
      if (overlap) {
        toast({
          heading: getLocalizedText("rentmatch.calendar_overlap_error_same"),
          variation: "error",
        });
        return;
      }

      if (overlapsBusyForUser(start, snappedEnd, activeBroker.id)) {
        toast({
          heading: getLocalizedText("rentmatch.calendar_overlap_agenda_error"),
          variation: "error",
        });
        return;
      }

      const titlePrefix = propertyAddress
        ? `${getLocalizedText(
            "rentmatch.calendar_placeholder_visit",
          )} ${propertyAddress}`
        : getLocalizedText("rentmatch.calendar_placeholder_visit");
      const newSlot: ICalendarSlot = {
        id: `slot-${Date.now()}`,
        title: `${titlePrefix} (${activeBroker.name})`,
        start,
        end: snappedEnd,
        userId: activeBroker.id,
        color: activeBroker.color,
        isReadonly: false,
        bookingAnchor: defaultBookingAnchor,
      };

      const newEvents = [...events, newSlot];
      setEvents(newEvents);
      syncToParent(newEvents);
      if (onDirty) onDirty();
    },
    [
      activeBroker,
      events,
      editableSlots,
      syncToParent,
      snapEndToDuration,
      onDirty,
      overlapsBusyForUser,
      defaultBookingAnchor,
    ],
  );

  // Change which free moments one block offers. Only that block: the whole
  // point of moving this out of the form at the top is that a Saturday of
  // back to back visits and an open weekday evening can live side by side.
  const setSlotAnchor = useCallback(
    (slotId: string, anchor: TBookingAnchor) => {
      const newEvents = events.map(e =>
        e.id === slotId ? { ...e, bookingAnchor: anchor } : e,
      );
      setEvents(newEvents);
      syncToParent(newEvents);
      if (onDirty) onDirty();
    },
    [events, syncToParent, onDirty],
  );

  // Delete a single slot by id
  const deleteSlot = useCallback(
    (slotId: string) => {
      const newEvents = events.filter(e => e.id !== slotId);
      setEvents(newEvents);
      syncToParent(newEvents);
      if (onDirty) onDirty();
    },
    [events, syncToParent, onDirty],
  );

  // Handle resizing of an existing slot
  const handleEventResize = useCallback(
    ({ event, start, end }: any) => {
      if (event.isReadonly) return;

      // Force the new size to snap to a multiple of configured duration
      const snappedEnd = snapEndToDuration(start, end);

      // Disallow overlap with existing blocks of any broker
      const overlap = editableSlots.some(
        s =>
          s.id !== event.id &&
          moment(start).isBefore(s.end) &&
          moment(snappedEnd).isAfter(s.start),
      );
      if (overlap) {
        toast({
          heading: getLocalizedText("rentmatch.calendar_overlap_error_other"),
          variation: "error",
        });
        return;
      }

      if (overlapsBusyForUser(start, snappedEnd, event.userId)) {
        toast({
          heading: getLocalizedText("rentmatch.calendar_overlap_agenda_error"),
          variation: "error",
        });
        return;
      }

      const newEvents = events.map(e =>
        e.id === event.id ? { ...e, start, end: snappedEnd } : e,
      );
      setEvents(newEvents);
      syncToParent(newEvents);
      if (onDirty) onDirty();
    },
    [
      events,
      editableSlots,
      syncToParent,
      snapEndToDuration,
      onDirty,
      overlapsBusyForUser,
    ],
  );

  // Handle moving of an existing slot
  const handleEventDrop = useCallback(
    ({ event, start, end }: any) => {
      if (event.isReadonly) return;
      const snappedEnd = snapEndToDuration(start, end);

      const overlap = editableSlots.some(
        s =>
          s.id !== event.id &&
          moment(start).isBefore(s.end) &&
          moment(snappedEnd).isAfter(s.start),
      );
      if (overlap) {
        toast({
          heading: getLocalizedText("rentmatch.calendar_overlap_error_other"),
          variation: "error",
        });
        return;
      }

      if (overlapsBusyForUser(start, snappedEnd, event.userId)) {
        toast({
          heading: getLocalizedText("rentmatch.calendar_overlap_agenda_error"),
          variation: "error",
        });
        return;
      }

      const newEvents = events.map(e =>
        e.id === event.id ? { ...e, start, end: snappedEnd } : e,
      );
      setEvents(newEvents);
      syncToParent(newEvents);
      if (onDirty) onDirty();
    },
    [
      events,
      editableSlots,
      syncToParent,
      snapEndToDuration,
      onDirty,
      overlapsBusyForUser,
    ],
  );

  const eventStyleGetter = (event: any) => {
    const style: React.CSSProperties = {
      backgroundColor: event.color,
      borderColor: event.color,
    };
    return { style, className: event.isReadonly ? "readonly" : "" };
  };

  if (isLoadingBrokers) {
    return (
      <PageLayout>
        <div style={{ textAlign: "center", padding: "40px" }}>
          <TextStyle variation="subdued">
            {getLocalizedText("rentmatch.calendar_loading_agendas")}
          </TextStyle>
        </div>
      </PageLayout>
    );
  }

  if (hasBrokerLoadError) {
    return (
      <PageLayout>
        <div style={{ textAlign: "center", padding: "40px" }}>
          <TextStyle variation="negative">
            {getLocalizedText("rentmatch.calendar_load_error")}
          </TextStyle>
        </div>
      </PageLayout>
    );
  }

  // Since DEV-3320 the roster is every office employee, so this only renders
  // when the API returns no employees and the property holds no saved moments
  // either. It says where to connect a calendar and offers no button for it
  // (Jonas, 2026-10-07).
  if (brokers.length === 0) {
    return (
      <PageLayout>
        <div style={{ textAlign: "center", padding: "40px" }}>
          <TextStyle variation="strong">
            {getLocalizedText("rentmatch.calendar_no_agendas")}
          </TextStyle>
          <br />
          <TextStyle variation="subdued" size="small">
            {getLocalizedText("rentmatch.calendar_connect_hint")}
          </TextStyle>
        </div>
      </PageLayout>
    );
  }

  /**
   * A moment whose colleague cannot take it: either nobody is assigned, or
   * the person has left the office. Both are repaired the same way, by
   * picking a colleague, so they differ only in what the marker says.
   */
  const getSlotEmployeeProblem = (slot: ICalendarSlot) => {
    if (!slot.userId) {
      return "missing" as const;
    }
    const broker = brokers.find(b => b.id === slot.userId);
    if (!broker || broker.hasLeftOffice) {
      return "left" as const;
    }
    return null;
  };

  const assignableBrokers = allConnections.filter(b => !b.hasLeftOffice);

  const handleConfirmReassign = () => {
    if (!slotToReassignId || !reassignToBrokerId) {
      return;
    }
    setEvents(prev => {
      const next = prev.map(e =>
        e.id === slotToReassignId ? { ...e, userId: reassignToBrokerId } : e,
      );
      syncToParent(next);
      return next;
    });
    if (onDirty) onDirty();
    setSlotToReassignId(null);
    setReassignToBrokerId("");
  };

  const pendingBroker = pendingRemoveBrokerId
    ? brokers.find(b => b.id === pendingRemoveBrokerId)
    : null;
  const pendingBrokerHasSlots = pendingRemoveBrokerId
    ? editableSlots.some(s => s.userId === pendingRemoveBrokerId)
    : false;

  // Total-card label: booked count for unlimited capacity, a "fully booked"
  // message when every bookable spot is taken, else the remaining capacity
  // with correct singular/plural.
  // Nothing bookable while blocks are drawn. Guarded on capacity, because
  // unlimited capacity adds 0 per window and would otherwise read as empty on a
  // perfectly good week.
  const hasNoBookableCapacity = capacity > 0 && totalCounts.theoretical === 0;

  let totalCardLabel: string;
  if (capacity <= 0) {
    totalCardLabel = `${getLocalizedText("rentmatch.calendar_total")}: ${
      totalCounts.booked
    } ${getLocalizedText("rentmatch.calendar_visits_booked_unlimited_short")}`;
  } else if (
    totalCounts.theoretical > 0 &&
    totalCounts.booked >= totalCounts.theoretical
  ) {
    totalCardLabel = getLocalizedText("rentmatch.calendar_slots_full");
  } else {
    totalCardLabel = `${getLocalizedText(
      "rentmatch.calendar_total",
    )}: ${getLocalizedText("rentmatch.calendar_visitors_possible_count", {
      count: String(totalCounts.theoretical),
      s: totalCounts.theoretical === 1 ? "" : "s",
    })}`;
  }

  return (
    <>
      {slotToReassignId && (
        <Modal
          heading={getLocalizedText(
            "rentmatch.calendar.assign_employee.heading",
          )}
          onClose={() => setSlotToReassignId(null)}
          actions={[
            {
              content: getLocalizedText("system.cancel"),
              onClick: () => setSlotToReassignId(null),
              appearance: "outline",
            },
            {
              content: getLocalizedText("system.save"),
              onClick: handleConfirmReassign,
              isDisabled: !reassignToBrokerId,
            },
          ]}
        >
          <div style={{ marginBottom: "12px" }}>
            <TextStyle variation="subdued">
              {getLocalizedText("rentmatch.calendar.assign_employee.info")}
            </TextStyle>
          </div>
          <SelectInput
            value={reassignToBrokerId}
            onChange={e => setReassignToBrokerId(e.target.value)}
          >
            <option value="">-</option>
            {assignableBrokers.map(b => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </SelectInput>
        </Modal>
      )}

      {pendingRemoveBrokerId && (
        <Modal
          heading={getLocalizedText("rentmatch.calendar_remove_broker_heading")}
          onClose={() => setPendingRemoveBrokerId(null)}
          actions={[
            {
              content: getLocalizedText("system.cancel"),
              appearance: "outline",
              onClick: () => setPendingRemoveBrokerId(null),
            },
            {
              content: getLocalizedText(
                "rentmatch.calendar_remove_broker_confirm",
              ),
              appearance: "primary",
              onClick: confirmRemoveBroker,
            },
          ]}
        >
          <TextStyle>
            {pendingBrokerHasSlots
              ? getLocalizedText(
                  "rentmatch.calendar_remove_broker_body_with_slots",
                  { name: pendingBroker?.name ?? "" },
                )
              : getLocalizedText("rentmatch.calendar_remove_broker_body", {
                  name: pendingBroker?.name ?? "",
                })}
          </TextStyle>
        </Modal>
      )}
      <PageLayout>
        <ControlsHeader>
          <FormRow>
            <FormGroup>
              <FormLabel>
                {getLocalizedText("rentmatch.calendar_slot_duration")}
              </FormLabel>
              <SelectInput
                value={duration}
                onChange={e => {
                  setDuration(Number(e.target.value));
                  if (onDirty) onDirty();
                }}
              >
                {SLOT_DURATIONS.map(d => (
                  <option key={d} value={d}>
                    {`${d} min`}
                  </option>
                ))}
              </SelectInput>
            </FormGroup>
            <FormGroup>
              <FormLabel>
                {getLocalizedText("rentmatch.calendar_visitors_per_slot")}
              </FormLabel>
              <SelectInput
                value={capacity}
                onChange={e => {
                  setCapacity(Number(e.target.value));
                  if (onDirty) onDirty();
                }}
              >
                <option value={1}>
                  1 {getLocalizedText("rentmatch.calendar_visitor")}
                </option>
                <option value={2}>
                  2 {getLocalizedText("rentmatch.calendar_visitors")}
                </option>
                <option value={3}>
                  3 {getLocalizedText("rentmatch.calendar_visitors")}
                </option>
                <option value={5}>
                  5 {getLocalizedText("rentmatch.calendar_visitors")}
                </option>
                <option value={10}>
                  10 {getLocalizedText("rentmatch.calendar_visitors")}
                </option>
              </SelectInput>
            </FormGroup>
            <CalendarsGroup>
              <FormLabel>
                {getLocalizedText("rentmatch.calendar_agendas_label")}
              </FormLabel>
              <PillsRow>
                <PillsContainer>
                  {brokers.map(b => (
                    <Pill
                      key={b.id}
                      active={activeBrokerId === b.id}
                      color={b.color}
                    >
                      {/* Amber, and the same triangle as everywhere else: a
                        colleague who has left the office still holds visit
                        moments nobody can keep, which is a real problem
                        unlike the missing calendar beside it. */}
                      {b.hasLeftOffice && (
                        <span
                          title={getLocalizedText(
                            "rentmatch.calendar.slot.employee_left",
                          )}
                          style={{ marginRight: "4px", display: "inline-flex" }}
                        >
                          <WarningTriangle size={14} />
                        </span>
                      )}
                      <PillSelect onClick={() => setActiveBrokerId(b.id)}>
                        {b.name}
                      </PillSelect>
                      {/* On the calendar it is about, rather than on a line
                        under the whole row where nothing tied it to either
                        name. Muted rather than amber: a colleague without a
                        connected calendar can still be given visit moments
                        and everything works, so this is a fact worth seeing
                        rather than a problem (Jonas, 2026-10-03). */}
                      {!b.hasConnectedCalendar && (
                        <WarningTooltip
                          size={14}
                          tone="muted"
                          // The pill has its own gap; the tooltip's default
                          // spacing on top of it pushed the marker away from
                          // the name it belongs to.
                          spacingLeft={0}
                          tooltipContent={
                            <>
                              <strong>
                                {getLocalizedText(
                                  "rentmatch.calendar.employee.no_calendar.label",
                                )}
                              </strong>
                              <div>
                                {getLocalizedText(
                                  "rentmatch.calendar.employee.no_calendar.info",
                                )}
                              </div>
                              {/* Where to connect it, in words: this modal
                                  offers no button for it (Jonas,
                                  2026-10-07, DEV-3209). */}
                              <div>
                                {getLocalizedText(
                                  b.id === String(currentUser?.id)
                                    ? "rentmatch.calendar.employee.no_calendar.connect_own"
                                    : "rentmatch.calendar.employee.no_calendar.connect_colleague",
                                )}
                              </div>
                            </>
                          }
                        />
                      )}
                      {brokers.length > 1 && (
                        <RemovePillButton
                          onClick={e => {
                            e.stopPropagation();
                            removeBrokerFromCalendar(b.id);
                          }}
                        >
                          ✕
                        </RemovePillButton>
                      )}
                    </Pill>
                  ))}
                </PillsContainer>
                {availableBrokersToAdd.length > 0 && (
                  <AddPillWrapper ref={dropdownRef}>
                    <AddPillButton
                      // A new key rather than a new value on the old one:
                      // this screen is not live yet, and editing the live key
                      // would put the new label on today's production button
                      // at the next bundle refresh of any release.
                      title={getLocalizedText(
                        "rentmatch.calendar.add_employee",
                      )}
                      onClick={() => setShowBrokerDropdown(!showBrokerDropdown)}
                    >
                      +
                    </AddPillButton>
                    {showBrokerDropdown && (
                      <BrokerDropdown>
                        {availableBrokersToAdd.map(c => (
                          <DropdownItem
                            key={c.id}
                            onClick={() => addBrokerToCalendar(c)}
                          >
                            <DropdownDot color={c.color} />
                            {c.name}
                            {/* The same muted marker as in the pills, so the
                                row says which colleagues have a calendar
                                connected without claiming anything is wrong.
                                A dropdown row is a button, so the explanation
                                stays on the native title here. */}
                            {!c.hasConnectedCalendar && (
                              <span
                                title={getLocalizedText(
                                  "rentmatch.calendar.employee.no_calendar.label",
                                )}
                                style={{
                                  marginLeft: "6px",
                                  display: "inline-flex",
                                }}
                              >
                                <WarningTriangle size={14} tone="muted" />
                              </span>
                            )}
                          </DropdownItem>
                        ))}
                      </BrokerDropdown>
                    )}
                  </AddPillWrapper>
                )}
              </PillsRow>
            </CalendarsGroup>
          </FormRow>
        </ControlsHeader>

        <CalendarWrapper>
          <CalendarContainer>
            <DnDCalendar
              localizer={localizer}
              culture={
                String(getLocale()) === "en_US"
                  ? "en-gb"
                  : String(getLocale()).replace("_", "-").toLowerCase()
              }
              formats={{
                dayFormat: "ddd D",
                dayRangeHeaderFormat: ({ start, end }: any) => {
                  if (moment(start).isSame(moment(end), "month")) {
                    return `${moment(start).format("D")} – ${moment(end).format(
                      "D MMMM",
                    )}`;
                  } else if (moment(start).isSame(moment(end), "year")) {
                    return `${moment(start).format("D MMMM")} – ${moment(
                      end,
                    ).format("D MMMM")}`;
                  } else {
                    return `${moment(start).format("D MMM YYYY")} – ${moment(
                      end,
                    ).format("D MMM YYYY")}`;
                  }
                },
              }}
              messages={{
                today: getLocalizedText("system.today"),
                previous: getLocalizedText("system.previous"),
                next: getLocalizedText("system.next"),
                month: getLocalizedText("system.month"),
                week: getLocalizedText("system.week"),
                day: getLocalizedText("system.day"),
                agenda: getLocalizedText("system.agenda"),
                date: getLocalizedText("system.date"),
                time: getLocalizedText("system.time"),
                event: getLocalizedText("system.event"),
              }}
              events={events}
              draggableAccessor={(event: any) => !event.isReadonly}
              resizableAccessor={(event: any) => !event.isReadonly}
              startAccessor={(event: any) => event.start}
              endAccessor={(event: any) => event.end}
              defaultView="week"
              views={["week", "day"]}
              selectable={true}
              resizable={true}
              onSelectSlot={handleSelectSlot}
              onEventResize={handleEventResize}
              onEventDrop={handleEventDrop}
              eventPropGetter={eventStyleGetter}
              components={{ event: CalendarEvent }}
              step={15}
              timeslots={4}
              min={new Date(0, 0, 0, 8, 0, 0)}
              max={new Date(0, 0, 0, 22, 0, 0)}
            />
          </CalendarContainer>

          <Sidebar>
            <SidebarTitle>
              {getLocalizedText("rentmatch.calendar_selected_slots")}
            </SidebarTitle>

            <SidebarSlotsList>
              {(() => {
                if (isLoadingEvents) {
                  return (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        padding: "30px 0",
                        gap: "12px",
                      }}
                    >
                      <Loading size={ELoadingSize.Small} />
                      <div
                        style={{ color: RM_COLORS.gray400, fontSize: "13px" }}
                      >
                        {getLocalizedText("rentmatch.calendar_loading_slots")}
                      </div>
                    </div>
                  );
                }
                if (slotsByDay.length === 0) {
                  return (
                    <div
                      style={{
                        color: RM_COLORS.gray400,
                        fontSize: "13px",
                        textAlign: "center",
                        padding: "20px 0",
                      }}
                    >
                      {getLocalizedText("rentmatch.calendar_select_slots_hint")}
                    </div>
                  );
                }
                return null;
              })()}

              {slotsByDay.map(day => {
                let dayTheoretical = 0;
                let dayBooked = 0;
                day.slots.forEach(s => {
                  const counts = countRemainingSlots(s);
                  dayTheoretical += counts.theoretical;
                  dayBooked += counts.booked;
                });

                return (
                  <DayCard key={day.dayKey}>
                    <DayHeader>
                      <div>
                        <DayLabel>{day.label}</DayLabel>
                        <div
                          style={{
                            fontSize: "13px",
                            fontWeight: 600,
                            color: RM_COLORS.gray900,
                          }}
                        >
                          {capacity > 0
                            ? getLocalizedText(
                                "rentmatch.calendar_visits_booked_of",
                                {
                                  booked: String(dayBooked),
                                  total: String(dayTheoretical),
                                },
                              ) ||
                              `${dayBooked} of the ${dayTheoretical} visits booked`
                            : getLocalizedText(
                                "rentmatch.calendar_visits_booked_unlimited",
                                { booked: String(dayBooked) },
                              ) || `${dayBooked} visits booked (unlimited)`}
                        </div>
                      </div>
                    </DayHeader>
                    {day.slots.map(slot => {
                      const slotCounts = countRemainingSlots(slot);
                      const isExpired = moment(slot.end).isSameOrBefore(
                        moment(),
                      );
                      // theoretical === 0 on a non-expired slot means every
                      // window overlaps a busy calendar event; on an expired
                      // slot it just means the slot lies in the past — those
                      // need different messaging.
                      const isFullyBlocked =
                        !isExpired && slotCounts.theoretical === 0;
                      let slotWarning: string | null = null;
                      if (isExpired) {
                        slotWarning = getLocalizedText(
                          "rentmatch.calendar_past_slot_tooltip",
                        );
                      } else if (isFullyBlocked) {
                        slotWarning = getLocalizedText(
                          "rentmatch.calendar_busy_conflict_tooltip",
                        );
                      }
                      return (
                        <SlotItem key={slot.id}>
                          <SlotItemRow>
                            <span>
                              {moment(slot.start).format("HH:mm")} –{" "}
                              {moment(slot.end).format("HH:mm")}
                              {slotWarning && (
                                <WarningTooltip tooltipContent={slotWarning} />
                              )}
                              <span
                                style={{
                                  marginLeft: "8px",
                                  fontSize: "12px",
                                  color: RM_COLORS.gray500,
                                  fontWeight: 400,
                                }}
                              >
                                {brokers.find(b => b.id === slot.userId)
                                  ?.name || "Broker"}
                              </span>
                              {(() => {
                                const problem = getSlotEmployeeProblem(slot);
                                if (!problem) {
                                  return null;
                                }
                                return (
                                  <span
                                    role="button"
                                    tabIndex={0}
                                    title={getLocalizedText(
                                      problem === "missing"
                                        ? "rentmatch.calendar.slot.employee_missing"
                                        : "rentmatch.calendar.slot.employee_left",
                                    )}
                                    onClick={() => {
                                      setSlotToReassignId(slot.id);
                                      setReassignToBrokerId("");
                                    }}
                                    onKeyDown={event => {
                                      if (event.key === "Enter") {
                                        setSlotToReassignId(slot.id);
                                        setReassignToBrokerId("");
                                      }
                                    }}
                                    style={{
                                      marginLeft: "6px",
                                      cursor: "pointer",
                                      display: "inline-flex",
                                    }}
                                  >
                                    <WarningTriangle size={14} />
                                  </span>
                                );
                              })()}
                            </span>
                            <DeleteSlotBtn
                              onClick={() => deleteSlot(slot.id)}
                              title={getLocalizedText(
                                "rentmatch.calendar_delete_slot",
                              )}
                            >
                              ✕
                            </DeleteSlotBtn>
                          </SlotItemRow>
                          {/* Per block, under the moment it belongs to: the
                              one place a broker can see at a glance which of
                              their blocks fill up from one end and which are
                              left open (Jonas, 2026-10-06). */}
                          <SlotAnchorSelect
                            value={slot.bookingAnchor || defaultBookingAnchor}
                            title={getLocalizedText(
                              "rentmatch.calendar.booking_anchor.label",
                            )}
                            aria-label={getLocalizedText(
                              "rentmatch.calendar.booking_anchor.label",
                            )}
                            onChange={event =>
                              setSlotAnchor(
                                slot.id,
                                event.target.value as TBookingAnchor,
                              )
                            }
                          >
                            {BOOKING_ANCHORS.map(anchor => (
                              <option key={anchor} value={anchor}>
                                {getLocalizedText(BOOKING_ANCHOR_KEY[anchor])}
                              </option>
                            ))}
                          </SlotAnchorSelect>
                        </SlotItem>
                      );
                    })}
                  </DayCard>
                );
              })}
            </SidebarSlotsList>

            {editableSlots.length > 0 && (
              <TotalCard $isEmpty={hasNoBookableCapacity}>
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: "15px",
                    color: hasNoBookableCapacity
                      ? RM_COLORS.redDark
                      : RM_COLORS.gray900,
                  }}
                >
                  {totalCardLabel}
                </div>
                {/* Says why, because "0" alone reads as a setting rather than
                    as a problem. Drawing over an appointment is refused now, so
                    the only way to land here is a block that was fine when it
                    was drawn and has since been covered or has passed. */}
                {hasNoBookableCapacity && (
                  <div
                    style={{
                      fontSize: "12px",
                      color: RM_COLORS.redDark,
                      marginTop: "4px",
                    }}
                  >
                    {getLocalizedText("rentmatch.calendar_no_capacity_reason")}
                  </div>
                )}
                {capacity > 0 && totalCounts.theoretical > 0 && (
                  <div
                    style={{
                      fontSize: "12px",
                      color: RM_COLORS.gray500,
                      marginTop: "2px",
                    }}
                  >
                    {`(${totalCounts.theoretical / capacity} ${getLocalizedText(
                      "rentmatch.calendar_slots_label",
                    )} × ${capacity} ${getLocalizedText(
                      "rentmatch.calendar_visitors_per_slot_short",
                    )})`}
                  </div>
                )}
              </TotalCard>
            )}
          </Sidebar>
        </CalendarWrapper>
      </PageLayout>
    </>
  );
};
